import { type ComponentType, memo } from "react";

/*
This is a helper component for check re-renders
The usage should be something like
export const DebuggedNurseContainer = withPropsDebugging(NurseContainer, "NurseContainer");
And then just replace <NurseContainer props.. /> with <DebuggedNurseContainer props.. />
Now you'll see the re-render reason within the console
*/

// Helper to do deep equality check
const isDeepEqual = (obj1: any, obj2: any): boolean => {
	if (obj1 === obj2) return true;

	if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;
	if (obj1 === null || obj2 === null) return false;

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) return false;

	return keys1.every((key) => {
		if (!Object.hasOwn(obj2, key)) return false;
		return isDeepEqual(obj1[key], obj2[key]);
	});
};

export function withPropsDebugging<TProps extends object>(WrappedComponent: ComponentType<TProps>, componentName: string) {
	return memo(
		(props: TProps) => {
			console.log(`${componentName} rendering`);
			return <WrappedComponent {...props} />;
		},
		(prevProps: TProps, nextProps: TProps) => {
			console.group(`${componentName} prop comparison:`);

			const allKeys = new Set([...Object.keys(prevProps), ...Object.keys(nextProps)]);
			let hasChanges = false;

			allKeys.forEach((key) => {
				const prevValue = prevProps[key as keyof TProps];
				const nextValue = nextProps[key as keyof TProps];

				if (prevValue !== nextValue) {
					hasChanges = true;
					console.group(`Prop '${key}' changed: ${typeof prevValue}, ${typeof nextValue}`);

					if (typeof prevValue === "object" && typeof nextValue === "object") {
						const referenceChanged = true;
						const valueChanged = !isDeepEqual(prevValue, nextValue);

						console.log("Reference changed:", referenceChanged);
						console.log("Values actually changed:", valueChanged);

						if (!valueChanged) {
							console.warn("⚠️ Only reference changed, values are the same! This might indicate an unnecessary re-render.");
							console.log("Suggestion: Check if this object is being recreated unnecessarily in the parent component.");
						} else {
							// Log the specific value changes if they're different
							console.log("From:", prevValue);
							console.log("To:", nextValue);
						}
					} else {
						console.log("From:", prevValue);
						console.log("To:", nextValue);
					}

					console.groupEnd();
				}
			});

			console.groupEnd();
			return false;
		},
	);
}
